
import RcDomainObject from "@/domain/common/RcDomainObject.js";
import ChecklistItem from "@/domain/model/checklist/item/CheckListItem.js";
import ChecklistItemOptionMap from "@/domain/model/checklist/item/option/CheckListItemOptionMap.js";

//import CheckListItemMap from "./item/CheckListItemMap.js";
//import CheckListMap from "./CheckListMap.js";
import StringUtils from '@/utils/StringUtils.js'
import CheckListItemOption from "@/domain/model/checklist/item/option/CheckListItemOption.js";
import CheckListSnapshot from "@/domain/model/checklist/version/CheckListSnapshot.js";
import ImageMap from "@/domain/model/image/ImageMap.js";

export default class ChecklistItemResult extends RcDomainObject {
  
  static MODEL_NAME = 'CheckListItemResult';
  
  static Fields = {
      ITEM: ChecklistItem.MODEL_NAME,
      ITEM_OPTIONS: ChecklistItemOptionMap.MODEL_NAME,
      REPAIRED_OPTIONS: "Repaired" + ChecklistItemOptionMap.MODEL_NAME,
      RESULT_TYPE: "resultType",
      REMARKS: "remarks",
      VALUE: "value",
      ITEM_NAME: "itemName",
      PICKED_OPTIONS: "pickedOptions",
      MINOR_REMARKS: "minorRemarks",
      MAJOR_REMARKS: "majorRemarks",
      IMAGES: ImageMap.MODEL_NAME,
  }
  
  static Values = {
      ResultTypes: {
        Initial: "initial",
        Enroute: "enroute",
      },
  }
  
  constructor(domain, data) {
    super(domain, data, ChecklistItemResult.MODEL_NAME);
  }

  fields() {
    return ChecklistItemResult.Fields;
  }
  
  find() {
    var result = this.domain.checklists().items().results().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    
    return result;
  }

  item() {
    var data = this.get(ChecklistItemResult.Fields.ITEM);
    return new ChecklistItem(this.domain, data);
  }
  
  withItem(item) {
    this.put(ChecklistItemResult.Fields.ITEM, item.data);
    return this;
  }

  options() {
    var data = this.get(ChecklistItemResult.Fields.ITEM_OPTIONS);
    return new ChecklistItemOptionMap(this.domain, data);
  }

  withOptions(options) {
    this.put(ChecklistItemResult.Fields.ITEM_OPTIONS, options.data);
    return this;
  }
  
  resultType() {
    return this.getString(ChecklistItemResult.Fields.RESULT_TYPE);
  }
  
  value() {
    return this.getString(ChecklistItemResult.Fields.VALUE);
  }
  
  remarks() {
    return this.getString(ChecklistItemResult.Fields.REMARKS);
  }
  
  hasRemarks() {
    return !StringUtils.isEmpty(this.remarks());
  }
  
  // What the driver wrote about the minor defects picked, kept apart from the major
  // ones because a schedule prints them in separate columns.
  minorRemarks() {
    return this.getString(ChecklistItemResult.Fields.MINOR_REMARKS);
  }
  
  withMinorRemarks(minorRemarks) {
    this.put(ChecklistItemResult.Fields.MINOR_REMARKS, minorRemarks);
    return this;
  }
  
  hasMinorRemarks() {
    return !StringUtils.isEmpty(this.minorRemarks());
  }
  
  majorRemarks() {
    return this.getString(ChecklistItemResult.Fields.MAJOR_REMARKS);
  }
  
  withMajorRemarks(majorRemarks) {
    this.put(ChecklistItemResult.Fields.MAJOR_REMARKS, majorRemarks);
    return this;
  }
  
  hasMajorRemarks() {
    return !StringUtils.isEmpty(this.majorRemarks());
  }
  
  // The photos taken against this answer.
  images() {
    var data = this.get(ChecklistItemResult.Fields.IMAGES);
    return new ImageMap(this.domain, data);
  }
  
  withImages(images) {
    this.put(ChecklistItemResult.Fields.IMAGES, images.data);
    return this;
  }
  
  hasImages() {
    return this.images().size() > 0;
  }
  
  isInitial() {
    return StringUtils.eic(ChecklistItemResult.Values.ResultTypes.Initial, this.resultType());
  }

  hasResultType() {
    return !StringUtils.isEmpty(this.resultType());
  }
  
  isEnroute() {
    // Compared without regard to case, matching isInitial above - the pair decides which
    // pass a result belongs to and they have to agree on how they read the stored value.
    return StringUtils.eic(ChecklistItemResult.Values.ResultTypes.Enroute, this.resultType());
  }
  
  pointer() {
    var data = {'@rid': this.id()};
    var result = new ChecklistItemResult(this.domain, data);
    return result;
  }
  
  copy() {
    var data = this._copy();
    return new ChecklistItemResult(this.domain, data);
  }

  // The question's name as it read when this answer was given, or null when the answer
  // was saved without one (before frozen copies existed).
  itemName() {
    if (!this.has(ChecklistItemResult.Fields.ITEM_NAME)) {
      return null;
    }
    return this.getString(ChecklistItemResult.Fields.ITEM_NAME);
  }

  // The choices picked, as they read when the answer was given - name, place and
  // severity - sorted by place. An empty list for a typed-in answer; null when the
  // answer was saved without them, or they cannot be read.
  pickedOptions() {
    if (!this.has(ChecklistItemResult.Fields.PICKED_OPTIONS)) {
      return null;
    }
    let raw = this.get(ChecklistItemResult.Fields.PICKED_OPTIONS);
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    if (!Array.isArray(raw)) {
      return null;
    }
    return raw
      .filter(entry => entry != null && typeof entry === 'object')
      .map(entry => CheckListSnapshot.toOption(this.domain, entry))
      .sort((a, b) => a.index() - b.index());
  }

  // The choices this answer picked, to show under its question. Read from the copy
  // taken with the answer when there is one, then from the frozen checklist, and only
  // then from the live choices - which may since have been renamed or removed.
  optionsAsAnswered(snapshot) {
    const picked = this.pickedOptions();
    if (picked != null) {
      return picked;
    }
    const live = this.options().find();
    if (snapshot == null) {
      return live.sortByIndex();
    }
    const itemId = this.item().id();
    return this.options().keys()
      .map(optionId => snapshot.option(itemId, optionId) || live.getCheckListItemOption(optionId))
      .filter(option => option.isNotNew())
      .sort((a, b) => a.index() - b.index());
  }

  // The name a repaired choice had when it was picked, falling back to its live name.
  optionNameAsAnswered(optionId) {
    const picked = this.pickedOptions();
    if (picked != null) {
      const found = picked.find(option => option.id() === optionId);
      if (found) {
        return found.name();
      }
    }
    return new CheckListItemOption(this.domain, optionId).find().name();
  }

  repairedItemOptions() {
    return new ChecklistItemOptionMap(this.domain, this.get(ChecklistItemResult.Fields.REPAIRED_OPTIONS));
  }

  isRepaired(itemOption) {
    // Asks whether the option is held, rather than whether the one we get back looks
    // saved: isNew() answers true for any option that has not been persisted, so a
    // repair recorded against an unsaved option never read back as repaired.
    return this.repairedItemOptions().has(itemOption.id());
  }
  
  addRepairedItemOption(itemOption) {
    let repairedOptions = this.repairedItemOptions();
    repairedOptions.add(itemOption);
    return this.withRepairedItemOptions(repairedOptions);
  }

  withRepairedItemOptions(repairedOptions) {
    this.put(ChecklistItemResult.Fields.REPAIRED_OPTIONS, repairedOptions.data);
    return this;
  }

  load() {
    this.withItem(this.item().find());
    this.withOptions(this.options().find());
    this.withRepairedItemOptions(this.repairedItemOptions().find());
  }
}